const POPUP_PREFIX = 'niwariPopup:'
export const POPUP_STATE_VERSION = 2
export const POPUP_STATE_TTL_MS = 24 * 60 * 60 * 1000

export const makePopupStateKey = (draftId, openedAt = Date.now()) =>
  `${POPUP_PREFIX}${String(draftId)}:${String(openedAt)}`

const isQuotaError = (error) =>
  error?.name === 'QuotaExceededError' || error?.code === 22

const safeParse = (raw) => {
  try {
    return raw ? JSON.parse(raw) : null
  } catch (_) {
    return null
  }
}

/**
 * Remove only allocation-popup keys that are expired, malformed, or from the
 * pre-v2 full-payload format. Other application storage is never touched.
 */
export const cleanupPopupStateKeys = (keepKeys = []) => {
  const keep = new Set(keepKeys.filter(Boolean))
  const removed = []

  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(POPUP_PREFIX) && !keep.has(key))
      .forEach((key) => {
        const value = safeParse(sessionStorage.getItem(key))
        const isLegacy =
          !value ||
          value.schemaVersion !== POPUP_STATE_VERSION ||
          Object.prototype.hasOwnProperty.call(value, 'inventoryData')
        const expiresAt = Number(value?.expiresAt)
        const isExpired = !Number.isFinite(expiresAt) || expiresAt < Date.now()

        if (isLegacy || isExpired) {
          sessionStorage.removeItem(key)
          removed.push(key)
        }
      })
  } catch (_) {
    // A storage read failure must not prevent the popup from loading from API.
  }

  return removed
}

export const readPopupState = (key, expected = {}) => {
  if (!key) return { status: 'missing', value: null }

  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return { status: 'missing', value: null }

    const value = safeParse(raw)
    if (!value || typeof value !== 'object') {
      sessionStorage.removeItem(key)
      return { status: 'invalid', value: null }
    }

    // Keep old payloads available for a one-release in-memory fallback. The
    // popup may replace them with the compact v2 shape after API loading.
    if (value.schemaVersion !== POPUP_STATE_VERSION) {
      return { status: 'legacy', value }
    }

    if (Number(value.expiresAt) < Date.now()) {
      sessionStorage.removeItem(key)
      return { status: 'expired', value: null }
    }

    if (
      expected.draftId !== undefined &&
      String(value.draftId) !== String(expected.draftId)
    ) {
      return { status: 'mismatch', value: null }
    }

    if (
      expected.openedAt !== undefined &&
      String(value.openedAt) !== String(expected.openedAt)
    ) {
      return { status: 'mismatch', value: null }
    }

    return { status: 'ok', value }
  } catch (error) {
    return { status: 'error', value: null, error }
  }
}

export const writePopupState = (key, state) => {
  if (!key) return { ok: false, reason: 'missing-key' }

  const value = {
    schemaVersion: POPUP_STATE_VERSION,
    expiresAt: Date.now() + POPUP_STATE_TTL_MS,
    ...state
  }

  let serialized
  try {
    serialized = JSON.stringify(value)
  } catch (error) {
    return { ok: false, reason: 'serialize-error', error }
  }

  const tryWrite = () => {
    try {
      sessionStorage.setItem(key, serialized)
      const bytes =
        typeof Blob !== 'undefined' ? new Blob([serialized]).size : serialized.length
      return { ok: true, bytes }
    } catch (error) {
      return { ok: false, reason: isQuotaError(error) ? 'quota' : 'write-error', error }
    }
  }

  let result = tryWrite()
  if (!result.ok && result.reason === 'quota') {
    cleanupPopupStateKeys([key])
    result = tryWrite()
  }
  return result
}

export const removePopupState = (key) => {
  if (!key) return
  try {
    sessionStorage.removeItem(key)
  } catch (_) {}
}

export const safeSetSessionItem = (key, value) => {
  const tryWrite = () => {
    try {
      sessionStorage.setItem(key, value)
      return { ok: true }
    } catch (error) {
      return {
        ok: false,
        reason: isQuotaError(error) ? 'quota' : 'write-error',
        error
      }
    }
  }

  let result = tryWrite()
  if (!result.ok && result.reason === 'quota') {
    cleanupPopupStateKeys()
    result = tryWrite()
  }
  return result
}
